import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Accordion } from 'react-accessible-accordion';
import { useQuery } from 'react-query';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { AxiosError, AxiosInstance } from 'axios';
import styled from 'styled-components';

import { AccountService } from '../../../services/AccountService';
import { PageHeadline } from '../../../components/atoms';

import { useAuth } from '../../../store/auth';
import { RootState } from '../../../store';
import { useAppSelector } from '../../../hooks/hooksRedux';

import { errorToast } from '../../../utils/toasts';
import { errorMessageAdapter } from '../../../adapters';

import { RequestContent } from './RequestContent';
import { OnGoingContent } from './OngoingContent';
import { useApi } from '../../../services/hooks/apiHook';

import { defaultColors, defaultBorder } from '../../../styles/style-guide';

const StyledTabs = styled(Tabs)<{ $primaryColor?: string; $borderColor?: string }>`
  border-color: ${({ $borderColor }) => $borderColor || defaultBorder.primary};
  color: ${({ $primaryColor }) => $primaryColor || defaultColors.primary};

  .react-tabs__tab--selected {
    background-color: #f5f6fa;
    font-weight: bold;
    border-bottom: 2px solid ${({ $borderColor }) => $borderColor || defaultBorder.primary};
  }
`;

export const RequestScreen: React.FunctionComponent = () => {
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const colors = useAppSelector((state: RootState) => state.styleGuide.data.colors);
  const borderColors = useAppSelector((state: RootState) => state.styleGuide.data.border);

  const primaryColor = colors.primary || defaultColors.primary;
  const primaryBorderColor = borderColors.primary || defaultBorder.primary;

  const { company, token } = useAuth();
  const api: AxiosInstance = useApi();

  const headers = { 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` };
  const [isLanguageRefreshing, setIsLanguageRefreshing] = useState(false);
  const initialRenderRef = useRef(true);

  const getRequestForScreen = async () => {
    try {
      if (company && company.id) {
        const response = await AccountService.getRequests(api, company.id, { headers });
        return response;
      }
    } catch (error) {
      console.error('Request error ', error);
      errorToast(errorMessageAdapter(error as AxiosError));
    }
  };

  const getRequestForOnGoing = async () => {
    const pageNumber = 1;
    try {
      if (company && company.id) {
        const response = await AccountService.getForOnGoingRequest(api, company.id, pageNumber, { headers });
        return response;
      }
    } catch (error) {
      console.error('Request error ', error);
      errorToast(errorMessageAdapter(error as AxiosError));
    }
  };

  const responseRequest = useQuery(['getRequest', company?.id], getRequestForScreen, {
    enabled: !!company?.id && !!token,
    refetchOnWindowFocus: false
  });
  const responseOnGoing = useQuery(['getOnGoingRequest', company?.id], getRequestForOnGoing, {
    enabled: !!company?.id && !!token,
    refetchOnWindowFocus: false
  });

  const refetchRequests = responseRequest.refetch;
  const refetchOnGoing = responseOnGoing.refetch;

  useEffect(() => {
    if (!company?.id || !token) {
      return;
    }

    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    let cancelled = false;

    const refreshWithDelay = async () => {
      setIsLanguageRefreshing(true);
      await new Promise((resolve) => setTimeout(resolve, 700));
      if (cancelled) {
        return;
      }
      try {
        await Promise.all([refetchRequests(), refetchOnGoing()]);
      } finally {
        if (!cancelled) {
          setIsLanguageRefreshing(false);
        }
      }
    };

    void refreshWithDelay();

    return () => {
      cancelled = true;
    };
  }, [legalPersonalitySlug, company?.id, token, refetchRequests, refetchOnGoing]);

  return (
    <>
      <div className="mb-6">
        <PageHeadline title={t('labels.lblRequests')} />
      </div>

      <StyledTabs
        $borderColor={primaryBorderColor}
        $primaryColor={primaryColor}
        selectedTabClassName="bg-[#F5F6FA] font-bold border-b-[2px]"
      >
        <TabList>
          <Tab>{t('labels.lblRequests')}</Tab>
          <Tab>{t('labels.lblOnGoing')}</Tab>
        </TabList>

        <TabPanel>
          <Accordion>
            <RequestContent
              dataRequest={responseRequest}
              headersFetch={headers}
              companyID={company}
              isRefreshing={isLanguageRefreshing}
            />
          </Accordion>
        </TabPanel>

        <TabPanel>
          <Accordion>
            <OnGoingContent
              dataRequest={responseOnGoing}
              headersFetch={headers}
              companyID={company}
              isRefreshing={isLanguageRefreshing}
            />
          </Accordion>
        </TabPanel>
      </StyledTabs>
    </>
  );
};
